/* eslint-disable no-param-reassign */

import { useForm } from '@abyss/web/hooks/useForm';
import { Button } from '@abyss/web/ui/Button';
import { CheckboxGroup } from '@abyss/web/ui/CheckboxGroup';
import { Flex } from '@abyss/web/ui/Flex';
import { FormProvider } from '@abyss/web/ui/FormProvider';
import { Layout } from '@abyss/web/ui/Layout';
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { adobeLinkTrackEvent } from '../../../../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { adobeModalTrackEvent } from '../../../../../../../common/AdobeTagging/adobeModalTrackEvent';
import { ConstantsLagoon } from '../../../../../../../common/ConstantsLagoon';
import { getDefaultCompareAttributes } from '../../../../../../../frontends/ProviderSearch/routes/ViewAll/utils/getDefaultCompareAttributes';
import { useCoverageType } from '../../../../../../../hooks/useCoverageType';
import { useLagoon } from '../../../../../../../hooks/useLagoon';
import { useSmartChoice } from '../../../../../../../hooks/useSmartChoice';
import { CompareCheckboxInfo } from '../../utility/compareDrawerConstants';
import { getCheckboxLabel } from '../../utility/editCompareDrawer.util';
import {
  EditCompareStyledCheckBox,
  EditComparisonDrawerDescription,
  StyledDrawer,
  StyledSubHeading,
} from './EditCompareDrawerSmartChoice.style';

type Props = {
  items: CompareCheckboxInfo[];
  setItems: (a: CompareCheckboxInfo[]) => void;
  isEditCompareOpenSmartChoice: boolean;
  setIsEditCompareOpenSmartChoice: (a: boolean) => void;
  showMergedCompareAndShare: boolean;
};

export const getCheckboxLabelLagoon = (lagoonData, label) =>
  find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE[label],
  })?.message;

export const EditCompareDrawerSmartChoice = ({
  items,
  setItems,
  isEditCompareOpenSmartChoice,
  setIsEditCompareOpenSmartChoice,
  showMergedCompareAndShare,
}: Props) => {
  const { t } = useTranslation();
  const lagoonData = useLagoon('ui-messaging')();
  const coverageType = useCoverageType();
  const [localItems, setLocalItems] = React.useState(items);
  const isSmartChoice = useSmartChoice();
  useEffect(() => {
    if (isEditCompareOpenSmartChoice) {
      adobeModalTrackEvent('edit comparison');
    }
    if (isEditCompareOpenSmartChoice && isSmartChoice) {
      setLocalItems(items);
    } else {
      const ItemsSorted = items.sort((a, b) =>
        t(a.label).localeCompare(t(b.label))
      );
      setLocalItems(ItemsSorted);
    }
  }, [isEditCompareOpenSmartChoice, items]);

  useEffect(() => {
    const chatIcon = document.getElementById(
      'async-messaging-custom-chat-icon-holder'
    );

    if (isEditCompareOpenSmartChoice) {
      adobeModalTrackEvent('edit comparison');
      if (chatIcon) {
        chatIcon.style.display = 'none';
      }
    } else if (chatIcon) {
      chatIcon.style.display = '';
    }

    return () => {
      if (chatIcon) {
        chatIcon.style.display = '';
      }
    };
  }, [isEditCompareOpenSmartChoice]);

  const handleAdobeLinkTrackEditComparison = (buttonName: string) => {
    adobeLinkTrackEvent({
      name: buttonName,
      location: 'edit comparison',
      type: 'internal',
    });
  };
  const defaultCompareAttribute = getDefaultCompareAttributes(
    coverageType,
    showMergedCompareAndShare && isSmartChoice
  );
  const form = useForm({
    defaultValues: { 'checkbox-form': defaultCompareAttribute },
    values: {
      'checkbox-form': localItems
        .filter((checkbox) => checkbox.isChecked && !checkbox.isDisabled)
        .map((checkbox) => checkbox.name),
    },
  });

  const onSubmit = (data) => {
    if (isEditCompareOpenSmartChoice) {
      const changedTempItems = cloneDeep(localItems);
      const formData = data['checkbox-form'];
      changedTempItems.forEach((checkbox, index) => {
        const tempCheckBox = checkbox;
        tempCheckBox.isChecked = formData?.includes(checkbox.name) || false;
        changedTempItems[index] = tempCheckBox;
      });
      setItems(changedTempItems);
      setLocalItems(changedTempItems);
      setIsEditCompareOpenSmartChoice(false);

      // Format selected attributes for Adobe tracking
      const selectedAttributes = changedTempItems
        .filter((item) => item.isChecked)
        .reduce((acc, item) => {
          const heading = item.heading || '';
          if (!acc[heading]) {
            acc[heading] = [];
          }
          const label = isSmartChoice
            ? getCheckboxLabelLagoon(lagoonData, item.label)
            : getCheckboxLabel(t(item.label));
          acc[heading].push(label);
          return acc;
        }, {});
      const formattedAttributes = Object.entries(selectedAttributes)
        .map(([heading, labels]) => `${heading}: ${labels.join(', ')}`)
        .join(' | ');
      // Send Adobe track event
      adobeLinkTrackEvent({
        name: 'apply',
        location: 'edit comparison',
        type: 'internal',
        userSelections: formattedAttributes,
      });
    }
  };

  return isEditCompareOpenSmartChoice ? (
    <StyledDrawer
      data-auto-testid="smart-choice-edit-compare-drawer"
      data-testid="smart-choice-edit-compare-drawer"
      footer={
        <Layout.Group>
          <Button
            data-auto-testid="compare-providers-cancel"
            data-testid="compare-providers-cancel"
            onClick={() => {
              handleAdobeLinkTrackEditComparison('cancel');
              setIsEditCompareOpenSmartChoice(false);
            }}
            variant="outline"
          >
            {t('CANCEL')}
          </Button>
          <Button
            data-auto-testid="compare-providers-apply"
            data-testid="compare-providers-apply"
            disabled={!localItems.some((item) => item.isChecked)}
            onClick={() => {
              onSubmit(form.getValues());
            }}
          >
            {t('Apply')}
          </Button>
        </Layout.Group>
      }
      isOpen={isEditCompareOpenSmartChoice}
      onClose={() => {
        handleAdobeLinkTrackEditComparison('close');
        setIsEditCompareOpenSmartChoice(false);
      }}
      size={383}
      title={
        <React.Fragment>
          {t('Edit comparison')}
          <Flex
            align-items="center"
            css={{ width: '100%' }}
            justify-content="flex-start"
          >
            <Button
              css={{ paddingLeft: '0px' }}
              data-auto-testid="compare-providers-deselect-all"
              data-testid="compare-providers-deselect-all"
              onClick={() => {
                handleAdobeLinkTrackEditComparison('deselect all');
                const updatedItems = localItems.map((checkbox) => ({
                  ...checkbox,
                  isChecked: false,
                }));
                setLocalItems(updatedItems);
                form.setValue('checkbox-form', updatedItems);
              }}
              variant="tertiary"
            >
              {t('Deselect all')}
            </Button>
            <Button
              data-auto-testid="compare-providers-reset-all"
              data-testid="compare-providers-reset-all"
              onClick={() => {
                handleAdobeLinkTrackEditComparison('reset');
                form.reset();
                setLocalItems(items);
              }}
              variant="tertiary"
            >
              {t('Reset')}
            </Button>
          </Flex>
          <EditComparisonDrawerDescription>
            {t('EDIT_COMPARE_DESCRIPTION')}
          </EditComparisonDrawerDescription>
        </React.Fragment>
      }
    >
      <FormProvider
        data-auto-testid="compare-providers-edit-compare-form"
        data-testid="compare-providers-edit-compare-form"
        onSubmit={onSubmit}
        state={form}
      >
        <Flex alignItems="flex-start" css={{ gap: '16px' }} direction="column">
          <CheckboxGroup display="column" label="" model="checkbox-form">
            {localItems?.map((checkbox, index) => (
              <>
                {isSmartChoice && (
                  <>
                    {checkbox?.heading === 'basic' &&
                      localItems[index - 1]?.heading !== 'basic' && (
                        <StyledSubHeading data-testid="compare-basic-information-heading">
                          {t('Basic Information')}
                        </StyledSubHeading>
                      )}
                    {checkbox?.heading === 'quality' &&
                      localItems[index - 1]?.heading !== 'quality' && (
                        <StyledSubHeading data-testid="compare-quality-heading">
                          {t('Quality')}
                        </StyledSubHeading>
                      )}
                    {checkbox?.heading === 'benefits' &&
                      localItems[index - 1]?.heading !== 'benefits' && (
                        <StyledSubHeading data-testid="compare-benefits-heading">
                          {t('Benefits')}
                        </StyledSubHeading>
                      )}
                    {checkbox?.heading === 'convenience' &&
                      localItems[index - 1]?.heading !== 'convenience' && (
                        <StyledSubHeading data-testid="compare-convenience-heading">
                          {t('Convenience')}
                        </StyledSubHeading>
                      )}
                    {checkbox?.heading === 'personalFit' &&
                      localItems[index - 1]?.heading !== 'personalFit' && (
                        <StyledSubHeading data-testid="compare-personal-fit-heading">
                          {t('Personal fit')}
                        </StyledSubHeading>
                      )}
                  </>
                )}
                <EditCompareStyledCheckBox
                  css={{ margin: '8px 0' }}
                  data-auto-testid={`compare-providers-edit-compare-checkbox-${checkbox.name}`}
                  data-testid={`compare-providers-edit-compare-checkbox-${checkbox.name}`}
                  isChecked={checkbox.isChecked && !checkbox.isDisabled}
                  isDisabled={checkbox?.isDisabled}
                  key={`CheckBoxKey-${checkbox.name}`}
                  label={
                    isSmartChoice
                      ? getCheckboxLabelLagoon(lagoonData, checkbox.label)
                      : getCheckboxLabel(t(checkbox.label))
                  }
                  onChange={(e) => {
                    const updatedItems = localItems.map((item) =>
                      item.name === checkbox.name
                        ? { ...item, isChecked: e.target.checked }
                        : item
                    );
                    setLocalItems(updatedItems);
                  }}
                  value={checkbox.name}
                />
              </>
            ))}
          </CheckboxGroup>
        </Flex>
      </FormProvider>
    </StyledDrawer>
  ) : null;
};
